import { useState, useMemo } from "react";
import { Button, Form, Icon, Label, Message, Modal, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "./DataTable";

const EMPTY_OPTION = {
  bookingType: "OFFICIAL",
  code: "",
  label: "",
  order: 0,
  isActive: true,
};

const BOOKING_TYPE_OPTIONS = [
  { key: "OFFICIAL", text: "Official", value: "OFFICIAL" },
  { key: "NON_OFFICIAL", text: "Non-official", value: "NON_OFFICIAL" },
];

const STATUS_OPTIONS = [
  { key: "active", text: "Active", value: "active" },
  { key: "inactive", text: "Inactive", value: "inactive" },
];

const fieldError = (message) => (message ? { content: message, pointing: "below" } : false);

const slugifyCode = (label) =>
  label.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");

function OptionFormFields({ form, errors, onChange }) {
  return (
    <Form error={!!errors.save}>
      <Form.Group widths="equal">
        <Form.Select
          label="Booking Type"
          required
          options={BOOKING_TYPE_OPTIONS}
          value={form.bookingType}
          onChange={(_, data) => onChange("bookingType", data.value)}
        />
        <Form.Input
          label="Label"
          required
          placeholder="e.g. University"
          value={form.label}
          error={fieldError(errors.label)}
          onChange={(e) => onChange("label", e.target.value)}
        />
        <Form.Input
          label="Display Order"
          type="number"
          min="0"
          value={form.order}
          onChange={(e) => onChange("order", e.target.value)}
        />
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Select
          label="Status"
          options={STATUS_OPTIONS}
          value={form.isActive ? "active" : "inactive"}
          onChange={(_, data) => onChange("isActive", data.value === "active")}
        />
      </Form.Group>
      {errors.save && <Message negative content={errors.save} />}
    </Form>
  );
}

const BOOKING_TYPE_LABELS = { OFFICIAL: "Official", NON_OFFICIAL: "Non-official" };

const columnHelper = createColumnHelper();

export default function PaymentResponsibilityTab({ options, onAdd, onUpdate, onDelete }) {
  /* formModal: null | { mode: "add" } | { mode: "edit", option } */
  const [formModal, setFormModal] = useState(null);
  const [form, setForm] = useState(EMPTY_OPTION);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const updateForm = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "label") next.code = slugifyCode(v);
      return next;
    });
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const openAdd = () => {
    setForm(EMPTY_OPTION);
    setErrors({});
    setFormModal({ mode: "add" });
  };

  const openEdit = (option) => {
    setForm({
      bookingType: option.booking_type || "OFFICIAL",
      code: option.code || "",
      label: option.label || "",
      order: option.order || 0,
      isActive: option.is_active !== false,
    });
    setErrors({});
    setFormModal({ mode: "edit", option });
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim()) e.label = "Label is required.";
    else if (!form.code.trim()) e.label = "Label must contain at least one letter or number.";
    return e;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (formModal.mode === "edit") {
        await onUpdate(formModal.option.id, form);
      } else {
        await onAdd(form);
      }
      setFormModal(null);
    } catch (e) {
      setErrors({
        save: e.message || `Failed to ${formModal.mode === "edit" ? "update" : "add"} option.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await onDelete(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (e) {
      setDeleteError(e.message || "Failed to delete option.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("booking_type", {
        header: "Booking Type",
        cell: (info) => BOOKING_TYPE_LABELS[info.getValue()] || info.getValue(),
      }),
      columnHelper.accessor("label", { header: "Label" }),
      columnHelper.accessor("order", { header: "Display Order" }),
      columnHelper.accessor("is_active", {
        header: "Status",
        cell: (info) => (
          <Label color={info.getValue() !== false ? "green" : "grey"}>
            {info.getValue() !== false ? "Active" : "Inactive"}
          </Label>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: (info) => (
          <Button.Group size="small">
            <Button basic onClick={() => openEdit(info.row.original)}>
              <Icon name="edit" />
              Edit
            </Button>
            <Button
              basic
              negative
              onClick={() => {
                setDeleteError("");
                setDeleteConfirm(info.row.original);
              }}
            >
              <Icon name="trash" />
              Delete
            </Button>
          </Button.Group>
        ),
      }),
    ],
    [],
  );

  return (
    <div>
      <Segment basic vertical textAlign="right">
        <Button primary onClick={openAdd}>
          <Icon name="plus" />
          Add Option
        </Button>
      </Segment>

      <DataTable
        data={options}
        columns={columns}
        emptyMessage="No payment responsibility options found yet."
      />

      {/* Add / Edit Modal */}
      <Modal
        open={!!formModal}
        onClose={() => setFormModal(null)}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name={formModal?.mode === "edit" ? "edit" : "plus circle"} />
          {formModal?.mode === "edit" ? "Edit Payment Responsibility Option" : "Add Payment Responsibility Option"}
        </Modal.Header>
        <Modal.Content>
          <OptionFormFields form={form} errors={errors} onChange={updateForm} />
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setFormModal(null)}>
            Cancel
          </Button>
          <Button primary loading={saving} disabled={saving} onClick={handleSave}>
            <Icon name="save" />
            {saving
              ? "Saving…"
              : formModal?.mode === "edit"
                ? "Save Changes"
                : "Add Option"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        size="mini"
      >
        <Modal.Header>
          <Icon name="trash" /> Delete Option
        </Modal.Header>
        <Modal.Content>
          <p>
            Delete <strong>{deleteConfirm?.label}</strong>?
          </p>
          <p className="bhu-confirm-sub">This cannot be undone.</p>
          {deleteError && <Message negative content={deleteError} />}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button negative loading={deleting} disabled={deleting} onClick={handleDelete}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
